import React, { FC, useContext, useMemo, useRef, useState } from "react";
import { IonIcon } from "@ionic/react";
import { calendarOutline } from "ionicons/icons";

import { Context } from "../../../../store/teacher";
import SubmitterRow from "../../../../molecules/teacher/SubmitterRow";
import SubmitterCalendarPopover from "../../../../molecules/teacher/SubmitterCalendarPopover";

import styles from "./SubmitterList.module.scss";

export type SubmitterFilter = { mode: "all" } | { mode: "date"; date: string };

type Props = {
  calling: boolean;
  filter: SubmitterFilter;
  onChangeFilter: (filter: SubmitterFilter) => void;
  today: string;
  markedDates: string[];
  onChangeViewMonth: (year: number, month: number) => void;
};

const SubmitterList: FC<Props> = ({
  calling,
  filter,
  onChangeFilter,
  today,
  markedDates,
  onChangeViewMonth,
}) => {
  const { contextState } = useContext(Context);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const iconButtonRef = useRef<HTMLButtonElement>(null);

  const isAll = filter.mode === "all";
  const isToday = filter.mode === "date" && filter.date === today;

  const totalCount = useMemo(
    () =>
      contextState.gearboxStudents.reduce(
        (sum, s) => sum + (s.realizations_count ?? 0),
        0,
      ) + (contextState.realizations_count_of_left_students ?? 0),
    [
      contextState.gearboxStudents,
      contextState.realizations_count_of_left_students,
    ],
  );

  const selectedDate = filter.mode === "date" ? filter.date : null;

  const selectedDateLabel = useMemo(() => {
    if (!selectedDate) return null;
    const [, m, d] = selectedDate.split("-").map(Number);
    return `${m}/${d}`;
  }, [selectedDate]);

  return (
    <div className={styles.gearboxStudents}>
      <div className={styles.header}>
        <span className={styles.title}>提出者一覧</span>
        <div className={styles.actions}>
          {selectedDateLabel && (
            <span className={styles.selectedDateLabel}>
              {selectedDateLabel}
            </span>
          )}
          <button
            ref={iconButtonRef}
            type="button"
            className={styles.iconButton}
            aria-label="日付選択カレンダー"
            onClick={() => setIsCalendarOpen(open => !open)}
          >
            <IonIcon icon={calendarOutline} />
          </button>
          <div className={styles.segmented}>
            <button
              type="button"
              className={[styles.segBtn, isAll ? styles.active : ""].join(" ")}
              onClick={() => onChangeFilter({ mode: "all" })}
            >
              全て
            </button>
            <button
              type="button"
              className={[styles.segBtn, isToday ? styles.active : ""].join(
                " ",
              )}
              onClick={() => onChangeFilter({ mode: "date", date: today })}
            >
              本日
            </button>
          </div>
          {isCalendarOpen && (
            <SubmitterCalendarPopover
              selectedDate={selectedDate}
              markedDates={markedDates}
              onChangeViewMonth={onChangeViewMonth}
              onSelect={date => {
                onChangeFilter({ mode: "date", date });
                setIsCalendarOpen(false);
              }}
              onClose={() => setIsCalendarOpen(false)}
              anchorRef={iconButtonRef}
            />
          )}
        </div>
      </div>
      <p>
        <span>名前</span>
        <span>総Stock数 {totalCount}</span>
      </p>
      {!contextState.gearboxStudents.length && calling ? (
        <div className={styles.loading}>読み込み中</div>
      ) : contextState.gearboxStudents.length ? (
        <ul>
          {contextState.gearboxStudents.map(student => (
            <SubmitterRow
              key={student.id}
              fullName={student.full_name}
              count={student.realizations_count ?? 0}
            />
          ))}
          {!!contextState.realizations_count_of_left_students && (
            <li>
              <span>その他</span>
              <span>{contextState.realizations_count_of_left_students}</span>
            </li>
          )}
        </ul>
      ) : (
        <div className={styles.emptyData}>空のデータ。</div>
      )}
    </div>
  );
};

export default SubmitterList;
